import { type ApolloQueryResult } from '@apollo/client/core/index.js';
import { property } from 'lit/decorators.js';
import {
  IVSApiService,
  type QueryTypeEnum,
  IvsFragments,
  type ApolloClientOptionalOptions,
} from '@trp-dms-digital-shared/ds-ui-library-api';

import { PopBaseComponent } from '../../base/pop-base-component.js';
import { Constants as IvsPopConstants } from './ivs-api-base.constants.js';

/**
 * Base class for components which fetch data from the IVS API.
 * @attribute {'ALL'|'ETF'} [data-query-type='ALL'] type of query to run
 * @attribute {string} [data-fragment] - graphql fragment for response
 * @attribute {string} [data-ticker] - ticker required if query-type === ETF
 */
export class PopIvsApiBaseComponent extends PopBaseComponent {
  /**
   * @internal
   */
  // @ts-expect-error apiService set in method called by constructor
  apiService: IVSApiService;

  /**
   * @internal
   */
  endpoint: string = IvsPopConstants.ENDPOINT_DEV;

  /**
   * @internal
   */
  apikey: string = IvsPopConstants.APIKEY_DEV;

  /**
   * Optional Apollo client options
   */
  @property({
    attribute: false,
  })
  clientOptions?: ApolloClientOptionalOptions;

  /**
   * The type of query, either `ALL` or `ETF`
   */
  @property({
    attribute: IvsPopConstants.DATA_QUERY_TYPE,
    type: String,
  })
  queryType!: QueryTypeEnum;

  /**
   * The fragment used to fetch the data for the component.
   */
  @property({
    attribute: IvsPopConstants.DATA_FRAGMENT,
    type: String,
  })
  fragment!: string;

  /**
   * ETF ticker symbol
   */
  @property({
    attribute: IvsPopConstants.DATA_TICKER,
    type: String,
  })
  ticker?: string;

  constructor(clientOptions?: ApolloClientOptionalOptions) {
    super();
    this.queryType = this.queryType || 'ALL';
    this.fragment = this.fragment || IvsFragments.etfData;
    this.clientOptions = clientOptions || IvsPopConstants.CLIENT_DEFAULT_OPTS;
    this.setEndpointAndApiKey();
    this.setApiService();
  }

  /**
   * Ensures endpoint and apikey are populated, only retrieves
   *  and adds to properties, does not use or confirm they work
   */
  setEndpointAndApiKey(): void {
    let endpoint: string;
    let apikey: string;

    if (
      this.popConfiguration.getConfiguration(
        IvsPopConstants.POPCONFIG_ENDPOINT,
      ) !== undefined
    ) {
      // @ts-expect-error method may return `unknown`
      endpoint =
        this.popConfiguration.getConfiguration(
          IvsPopConstants.POPCONFIG_ENDPOINT,
        ) || '';
      // @ts-expect-error method may return `unknown`
      apikey =
        this.popConfiguration.getConfiguration(
          IvsPopConstants.POPCONFIG_APIKEY,
        ) || '';
    } else {
      endpoint =
        this.clientOptions?.uri?.toString() || IvsPopConstants.ENDPOINT_DEV;
      apikey =
        this.clientOptions?.headers?.apikey || IvsPopConstants.APIKEY_DEV;
    }

    this.endpoint = endpoint;
    this.apikey = apikey;
  }

  /**
   * Runs constructor on api service when clientOpts.ssrMode is true,
   *  which is the default, deferring `createClient` to the first call
   *  for data from the service.
   */
  private setApiService() {
    this.apiService = new IVSApiService(
      this.endpoint,
      this.apikey,
      this.clientOptions,
    );

    // super.processResponse() not working inside `subscribe`
    this.apiService.processResponse = this.processResponse;
  }

  /**
   * Uses given props to configure apiService
   *
   * note: need logger to replace console.warn:
   * @rally https://rally1.rallydev.com/#/?detail=/userstory/816373085855&fdp=true
   * @internal
   */
  protected processProps = (): void => {
    let missingRequiredProps = '';
    if (this.queryType === 'ETF' && !this.ticker) {
      missingRequiredProps += 'ticker required to show ETF\n';
    }
    if (!this.fragment) {
      missingRequiredProps += 'fragment required\n';
    }
    if (missingRequiredProps !== '') {
      console.warn(IvsPopConstants.LOG_PREFIX, missingRequiredProps);
      return;
    }

    this.apiService.queryType = this.queryType;

    if (this.queryType === 'ETF') {
      this.apiService.variables = {
        ticker: this.ticker,
      };
    } else {
      this.apiService.variables = {};
    }
  };

  /**
   * Overwrite if `componentObject` is different from the API response.
   * @internal
   */
  processResponse = ({ data, errors }: ApolloQueryResult<any>) => {
    if (errors) {
      console.error(IvsPopConstants.LOG_PREFIX, errors);
      return;
    }
    this.componentObject = data;
  };

  /**
   * Initializes the component by fetching data from the API and setting up the component object.
   * If the component object is already populated, this method does nothing.
   * @internal
   */
  protected initialize() {
    if (this.componentObject) {
      super.initialize();
      return;
    }

    this.setupApiSubscription('initialized');
    this.callApiService();
    super.initialize();
  }

  private setupApiSubscription(eventName: string) {
    // ensure apiService configured by props
    this.processProps();

    // create client if it does not exist
    if (!this.apiService.apolloClient) {
      this.apiService.createClient();
    }

    const apiServiceFetchSubscription = this.apiService
      .fetch()
      .subscribe((response) => {
        // Emit event to notify that the component has been initialized
        const renderedEvent = new CustomEvent(
          `pop:product:${this.tagName}:${eventName}`,
          {
            detail: {
              data: response,
            },
          },
        );

        if (typeof window !== 'undefined') {
          window.dispatchEvent(renderedEvent);
        }

        this.processResponse(response);

        apiServiceFetchSubscription.unsubscribe();
      });

    this.apiService.queryType = this.queryType;
  }

  /**
   * Ensures API setup and requests content
   */
  protected callApiService(eventName = 'refreshData') {
    this.setupApiSubscription(eventName);
    this.apiService.updateRequest(this.fragment);
  }
}
